import React, { useMemo } from "react";

import { useDisclosure } from "@mantine/hooks";
import {
  ActionIcon,
  Modal,
  Loader,
  Table,
  Divider,
  ScrollArea,
  Stack,
  Badge,
  Text,
} from "@mantine/core";
import { useGetAllElevesByParentQuery } from "../../redux/features/eleveApi";
import { useGetStudentActivityQuery } from "../../redux/features/diagnostiqueApi";
import { Eye } from "tabler-icons-react";
import ParentLoginStatsChart from "../../components/ParentLoginStatsChart";

function DetailsParent({ parent }) {
  const [opened, { open, close }] = useDisclosure(false);
  const { data: students, isLoading } = useGetAllElevesByParentQuery(
    parent.id,
    { skip: !parent.id }
  );

  // Récupérer les activités des enfants de ce parent
  const { data: activityData, isLoading: activityLoading } =
    useGetStudentActivityQuery(
      { parentId: parent.id },
      { skip: !parent.id || !opened }
    );

  // Mapper la dernière activité par studentId
  const lastActivityByStudent = useMemo(() => {
    if (!activityData?.data) return {};
    const map = {};
    // Les données sont triées par date décroissante, on prend la première par élève
    activityData.data.forEach((activity) => {
      const studentId = activity.student?.id;
      if (studentId && !map[studentId]) {
        map[studentId] = activity;
      }
    });
    return map;
  }, [activityData]);

  const formatDate = (activity) => {
    if (!activity?.date_diagnostic) return "";
    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    // Afficher l'heure uniquement si created_at est fiable :
    // on vérifie que created_at tombe le même jour que date_diagnostic
    // (les anciennes tentatives ont un created_at faux = date de migration)
    if (activity?.created_at) {
      const createdDate = new Date(activity.created_at);
      const diagDate = new Date(activity.date_diagnostic + "T00:00:00");
      const sameDay =
        createdDate.getFullYear() === diagDate.getFullYear() &&
        createdDate.getMonth() === diagDate.getMonth() &&
        createdDate.getDate() === diagDate.getDate();

      if (sameDay) {
        // C'est une vraie tentative récente, on affiche date + heure
        return createdDate.toLocaleDateString("fr-FR", {
          ...options,
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }

    // Ancienne tentative ou pas de created_at : date seule
    const date = new Date(activity.date_diagnostic + "T00:00:00");
    return date.toLocaleDateString("fr-FR", options);
  };

  const StatusBadge = ({ status }) => {
    if (status === "VALIDE") {
      return (
        <Badge color="green" size="sm" variant="filled">
          Validé
        </Badge>
      );
    }
    if (status === "ENCOURS") {
      return (
        <Badge color="orange" size="sm" variant="filled">
          En cours
        </Badge>
      );
    }
    return (
      <Badge color="gray" size="sm" variant="filled">
        {status}
      </Badge>
    );
  };

  const StudentByParent = () => {
    const rows = students?.data.map((data) => {
      const activity = lastActivityByStudent[data.id];

      return (
        <tr key={data.id}>
          <td>
            {data?.firstName} {data?.lastName}
          </td>
          <td>{data?.classe?.name}</td>
          <td>
            {activityLoading ? (
              <Loader size="xs" />
            ) : activity ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Text size="sm" weight={500}>
                    {activity.matiere}
                  </Text>
                  <StatusBadge status={activity.status} />
                </div>
                <Text size="xs" color="dimmed">
                  {formatDate(activity)}
                  {activity.score_max > 0 &&
                    ` — ${activity.score_total}/${activity.score_max} pts`}
                </Text>
              </div>
            ) : (
              <Text size="sm" color="dimmed" italic>
                Aucune activité
              </Text>
            )}
          </td>
        </tr>
      );
    });

    return (
      <div>
        <ScrollArea>
          <Table
            horizontalSpacing="md"
            verticalSpacing="xs"
            miw={124}
            sx={{ tableLayout: "fixed" }}
            bg={"#fff"}
          >
            <thead>
              <tr>
                <th>Elève</th>
                <th>Classe</th>
                <th>Dernière activité</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        </ScrollArea>
      </div>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        withCloseButton={true}
        size="xl"
        title={`Détails de : ${parent.fullName}`}
      >
        <Stack spacing="md">
          {/* Section enfants avec activité */}
          <div>
            <h4 style={{ marginBottom: 10 }}>Les enfants</h4>
            <Divider mb={10} />
            {isLoading ? (
              <Loader />
            ) : students?.data?.length === 0 ? (
              <p>Pas d'enfant</p>
            ) : (
              <StudentByParent />
            )}
          </div>

          {/* Section statistiques de connexion */}
          <ParentLoginStatsChart
            parentId={parent.id}
            parentName={parent.fullName}
          />
        </Stack>
      </Modal>

      <ActionIcon
        onClick={open}
        color="blue"
        size="md"
        variant="light"
        title="Voir les détails"
      >
        <Eye size={18} />
      </ActionIcon>
    </>
  );
}
export default DetailsParent;
